import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Download, Share2, RotateCcw, GripVertical, PartyPopper, Wand2, Check, Copy, Mail, Users } from "lucide-react";
import SmartInviteModal from "../invites/SmartInviteModal";
import LeadCaptureCard from "../LeadCaptureCard";
import AIImageWithDisclaimer from "../AIImageWithDisclaimer";

export const MagicRevealAnimation = ({ revealPhase }) => (
  <div className="mb-8 flex items-center justify-center" style={{ minHeight: 400 }}>
    <div className="text-center">
      {revealPhase === "preparing" && (
        <div className="animate-in fade-in duration-500">
          <div className="relative w-24 h-24 mx-auto mb-6">
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] animate-spin" style={{ animationDuration: '2s' }} />
            <div className="absolute inset-1 rounded-full bg-[#FFFAF5] flex items-center justify-center">
              <Wand2 size={32} className="text-[#E83E8C] animate-pulse" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-[#1E1B4B] mb-2">Getting the magic ready...</h2>
          <p className="text-[#94a3b8] text-sm">Your party decoration is almost here</p>
          <div className="flex items-center justify-center gap-3 mt-4">
            <div className="w-2 h-2 rounded-full bg-[#E83E8C] animate-bounce" style={{ animationDelay: '0s' }} />
            <div className="w-2 h-2 rounded-full bg-[#FF9F1C] animate-bounce" style={{ animationDelay: '0.2s' }} />
            <div className="w-2 h-2 rounded-full bg-[#8b5cf6] animate-bounce" style={{ animationDelay: '0.4s' }} />
          </div>
        </div>
      )}
      {revealPhase === "ready" && (
        <div className="animate-in zoom-in duration-500">
          <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] flex items-center justify-center shadow-lg shadow-[#E83E8C]/30">
            <PartyPopper size={36} className="text-white" />
          </div>
          <h2 className="text-3xl font-bold text-[#1E1B4B] mb-2">Ready for Magic!</h2>
          <p className="text-[#E83E8C] font-semibold text-sm animate-pulse">Unveiling your decorated room...</p>
        </div>
      )}
    </div>
  </div>
);

// ─── What's Included card — rendered SEPARATELY below the clean image preview ───
const WhatsIncludedCard = ({ result }) => {
  const items = result.package_items || [];
  const [copied, setCopied] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const shareUrl = result.job_id ? `${window.location.origin}/magic/${result.job_id}` : "";

  const copyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch { /* ignore */ }
  };

  if (!items.length && !result.package_price) return null;
  const price = result.package_price || 0;
  const displayName = result.package === "Custom" ? "Build Your Party" : result.package;

  return (
    <div
      className="mt-5 bg-white rounded-2xl border border-[#f0e6d9] shadow-sm overflow-hidden"
      data-testid="magic-whats-included"
    >
      {/* Header: package name + price pill */}
      <div className="relative px-5 py-4 bg-gradient-to-br from-[#FFFAF5] via-white to-[#E83E8C]/5 border-b border-[#f0e6d9]">
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#E83E8C] to-[#8b5cf6]" />
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-xl sm:text-2xl font-bold text-[#1E1B4B] leading-tight" data-testid="magic-package-name">
              {displayName}
            </h3>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#8b5cf6]/10 text-[#8b5cf6] text-[11px] font-semibold" data-testid="magic-occasion-pill">
                <Sparkles size={10} /> {result.occasion}
              </span>
              <span className="text-[11px] text-[#94a3b8]">AI-generated preview</span>
            </div>
          </div>
          {price > 0 && (
            <div
              className="flex-shrink-0 px-4 py-1.5 rounded-full bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white text-sm font-bold shadow-md shadow-[#E83E8C]/25"
              data-testid="magic-price-pill"
            >
              ₹ {price.toLocaleString()}
            </div>
          )}
        </div>
      </div>

      {/* Items list — bullet style with pink checks */}
      <div className="px-5 py-4">
        <p className="text-[11px] font-bold text-[#94a3b8] uppercase tracking-wider mb-3">
          What's included
        </p>
        <ul className="space-y-2.5" data-testid="magic-items-list">
          {items.map((item, i) => (
            <li key={`${item}-${i}`} className="flex items-start gap-2.5">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-br from-[#E83E8C] to-[#8b5cf6] flex items-center justify-center mt-0.5">
                <Check size={12} className="text-white" strokeWidth={3} />
              </span>
              <span className="text-sm text-[#1E1B4B] leading-snug">{item}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Copy party link CTA */}
      {shareUrl && (
        <div className="px-5 pb-4 pt-1 space-y-2">
          <button
            onClick={() => setInviteOpen(true)}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white text-xs font-semibold shadow-md shadow-[#E83E8C]/20 hover:opacity-90 transition-all"
            data-testid="magic-create-invite-btn"
          >
            <Mail size={14} /> Create AI Invitation for my guests
          </button>
          <button
            onClick={copyLink}
            className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border text-xs font-semibold transition-all ${
              copied
                ? "bg-emerald-50 border-emerald-200 text-emerald-600"
                : "bg-white border-[#E83E8C]/30 text-[#E83E8C] hover:bg-[#E83E8C]/5"
            }`}
            data-testid="magic-copy-party-link-btn"
          >
            {copied ? <><Check size={14} /> Link copied — paste anywhere</> : <><Copy size={14} /> Copy Party Link</>}
          </button>
          <a
            href={`/plan-with-friends?occasion=${encodeURIComponent(result.occasion || '')}`}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#1E1B4B] text-white text-xs font-semibold hover:bg-[#2d2866] transition-all"
            data-testid="magic-plan-with-friends-btn"
          >
            <Users size={14} /> Plan with Friends & Split Budget 💸
          </a>
          <p className="text-[10px] text-[#94a3b8] text-center">Anyone who opens this link sees the same preview</p>

          <SmartInviteModal
            open={inviteOpen}
            onClose={() => setInviteOpen(false)}
            defaults={{
              occasion: result.occasion,
              package: result.package,
              magic_job_id: result.job_id,
            }}
          />
        </div>
      )}
    </div>
  );
};

export const MagicResultView = ({
  result, uploadedImage, sliderPos, sliderContainerRef,
  handleSliderMouseDown, handleSliderTouchStart,
  handleDownload, handleShare, onReset, packages,
}) => {
  // Toggle to show the user's original photo alongside the AI preview — asked for in
  // the Feb 2026 docx: "AI Preview mode currently hides the before image".
  const [showOriginal, setShowOriginal] = React.useState(false);
  const originalSrc = uploadedImage || (result.source_image_b64 ? `data:image/jpeg;base64,${result.source_image_b64}` : "");
  return (
  <div className="mb-8" data-testid="magic-result">
    <style>{`
      @keyframes curtainReveal {
        0% { clip-path: inset(0 50% 0 50%); opacity: 0.5; }
        60% { clip-path: inset(0 5% 0 5%); opacity: 0.9; }
        100% { clip-path: inset(0 0 0 0); opacity: 1; }
      }
      .curtain-reveal { animation: curtainReveal 0.8s ease-out forwards; }
    `}</style>

    {/* ── CLEAN IMAGE PREVIEW (no elements sheet attached — that's added on download/share) ── */}
    <div className="bg-white rounded-2xl shadow-lg border border-[#f0e6d9] overflow-hidden curtain-reveal">
      {result.is_fallback && (
        <div className="px-4 py-2.5 bg-gradient-to-r from-[#FF9F1C]/15 to-[#E83E8C]/10 border-b border-[#FF9F1C]/30 flex items-center gap-2" data-testid="magic-fallback-banner">
          <Sparkles size={14} className="text-[#FF9F1C] flex-shrink-0" />
          <p className="text-xs text-[#1E1B4B] font-medium">
            Here's a <span className="font-bold">{result.package} {result.occasion}</span> preview — book to get this exact setup at home.
          </p>
        </div>
      )}
      {result.is_ai_preview && !result.is_fallback && (
        <div className="px-4 py-2.5 bg-gradient-to-r from-purple-500/15 to-[#E83E8C]/10 border-b border-purple-300 flex items-center gap-2" data-testid="magic-ai-preview-banner">
          <Sparkles size={14} className="text-purple-600 flex-shrink-0" />
          <p className="text-xs text-[#1E1B4B] font-medium">
            <span className="font-bold">AI-generated preview</span> — this is your {result.package} {result.occasion} style (not your exact room). Book to get this setup at home.
          </p>
        </div>
      )}
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#E83E8C]/5 to-purple-50 border-b border-[#f0e6d9]">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-[#E83E8C]" />
          <span className="text-sm font-semibold text-[#1E1B4B]">{result.package} {result.occasion} Decoration</span>
        </div>
        {!result.is_template && !result.is_fallback && !result.is_ai_preview && uploadedImage && (
          <span className="text-[10px] text-[#94a3b8] font-medium">Drag the slider to compare</span>
        )}
        {result.is_template && (
          <span className="text-[10px] text-[#94a3b8] font-medium">Template Preview</span>
        )}
        {result.is_fallback && (
          <span className="text-[10px] text-[#FF9F1C] font-medium">Preview</span>
        )}
        {result.is_ai_preview && !result.is_fallback && (
          <span className="text-[10px] text-purple-600 font-medium">AI Style</span>
        )}
      </div>

      {(result.is_template || result.is_fallback || result.is_ai_preview) ? (
        <>
          {showOriginal && originalSrc ? (
            <div className="grid grid-cols-2 gap-0 bg-black" data-testid="magic-ai-before-after">
              <div className="relative">
                <img src={originalSrc} alt="Your original room" className="w-full block object-cover" draggable="false" />
                <span className="absolute top-2 left-2 bg-black/70 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">BEFORE</span>
              </div>
              <AIImageWithDisclaimer
                wrapperClassName="relative"
                src={`data:image/jpeg;base64,${result.image_base64}`}
                alt={`${result.package} ${result.occasion} decoration preview`}
                className="w-full block object-cover"
                draggable="false"
                compact
              >
                <span className="absolute top-2 left-2 bg-[#E83E8C] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">AFTER</span>
              </AIImageWithDisclaimer>
            </div>
          ) : (
            <AIImageWithDisclaimer
              src={`data:image/jpeg;base64,${result.image_base64}`}
              alt={`${result.package} ${result.occasion} decoration preview`}
              className="w-full block"
              draggable="false"
            />
          )}
          {originalSrc && (
            <div className="px-4 py-2 bg-white border-t border-[#f0e6d9] flex items-center justify-between">
              <span className="text-[11px] text-[#94a3b8]">
                {showOriginal ? "Side-by-side comparison" : "AI preview only"}
              </span>
              <button
                onClick={() => setShowOriginal((v) => !v)}
                className="text-xs font-bold text-[#E83E8C] hover:underline flex items-center gap-1"
                data-testid="magic-toggle-before"
              >
                {showOriginal ? "Hide my photo" : "Show my photo (before)"}
              </button>
            </div>
          )}
        </>
      ) : (
        <div
          ref={sliderContainerRef}
          className="relative select-none cursor-col-resize overflow-hidden"
          style={{ minHeight: 300 }}
          onMouseDown={handleSliderMouseDown}
          onTouchStart={handleSliderTouchStart}
          data-testid="magic-before-after-slider"
        >
          <AIImageWithDisclaimer
            src={`data:image/jpeg;base64,${result.image_base64}`}
            alt="Decorated room"
            className="w-full max-h-[500px] object-cover bg-gray-50 block"
            draggable="false"
          />
          {/* BEFORE image = customer upload, deliberately NOT wrapped with
              the AI disclaimer. Clipped overlay rides on top of the AFTER. */}
          <div className="absolute inset-0 overflow-hidden" style={{ width: `${sliderPos}%` }}>
            <img
              src={uploadedImage}
              alt="Original room"
              className="max-h-[500px] object-cover bg-gray-100 block"
              style={{ width: sliderContainerRef.current ? `${sliderContainerRef.current.offsetWidth}px` : '100%', maxHeight: '500px', minHeight: '100%' }}
              draggable="false"
            />
          </div>
          <div className="absolute top-0 bottom-0 z-10" style={{ left: `${sliderPos}%`, transform: 'translateX(-50%)' }}>
            <div className="w-0.5 h-full bg-white shadow-lg" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-xl border-2 border-[#E83E8C] flex items-center justify-center cursor-col-resize">
              <GripVertical size={16} className="text-[#E83E8C]" />
            </div>
          </div>
          <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/60 text-white text-xs font-semibold pointer-events-none z-20">Before</div>
          <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-[#E83E8C]/90 text-white text-xs font-semibold pointer-events-none z-20">After</div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-t border-[#f0e6d9] bg-[#FFFAF5]/50">
        <button onClick={handleDownload} className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#E83E8C] text-white text-xs font-semibold hover:bg-[#d1357e] transition-all" data-testid="magic-download-btn">
          <Download size={14} /> Download HD
        </button>
        <button onClick={handleShare} className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#25D366] text-white text-xs font-semibold hover:bg-[#1da851] transition-all" data-testid="magic-share-btn">
          <Share2 size={14} /> Share on WhatsApp
        </button>
        <button onClick={onReset} className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-gray-200 text-[#475569] text-xs font-semibold hover:bg-gray-50 transition-all ml-auto" data-testid="magic-try-again-btn">
          <RotateCcw size={14} /> {(result.is_template || result.is_fallback || result.is_ai_preview) ? "Back to Gallery" : "Try Another Style"}
        </button>
      </div>
    </div>
    {/* ── Separate "What's Included" card (Creator Studio outcome style) ── */}
    <WhatsIncludedCard result={result} />

    {/* Lead capture — pipe Magic viewers into CRM */}
    <div className="max-w-md mx-auto mt-4">
      <LeadCaptureCard
        source="magic"
        extra={{
          package: result.package,
          occasion: result.occasion,
          is_template: !!result.is_template,
          is_ai_preview: !!result.is_ai_preview,
          job_id: result.job_id,
        }}
        headline="Want this exact decoration at your home?"
        subheadline="Drop your number — our team will WhatsApp a tailored quote within 24 hours."
        cta="Get My Quote"
      />
    </div>

    <div className="text-center mt-4">
      <p className="text-sm text-[#475569] mb-2">Love the look? Book this setup for your next party!</p>
      <Link to={`/?scrollTo=book-online&package=${encodeURIComponent(result.package)}&occasion=${encodeURIComponent(result.occasion)}`} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#E83E8C] text-white font-semibold text-sm hover:bg-[#d1357e] transition-all shadow-md" data-testid="magic-book-now-btn">
        <PartyPopper size={16} /> Book This Look — from Rs {packages.find(p => p.name === result.package)?.price?.toLocaleString()}
      </Link>
    </div>
  </div>
  );
};
